/**
 * Le texte d'un ticket pris en photo ou d'une facture en PDF, sans Pack IA :
 * une photo se lit dans le navigateur ; un PDF donne son texte au serveur, et
 * le navigateur prend le relais s'il n'en a pas.
 *
 * <p>Une photo passe par tesseract.js (français, allemand, italien, anglais),
 * servi depuis /ocr/ (scripts/copie-ocr.mjs) : la politique de sécurité du site
 * n'admet aucun CDN. Un PDF donne son texte par PDF.js ; scanné, sa première
 * page est dessinée puis lue comme une photo. Tout se charge au premier ticket
 * seulement.
 */

import { repriseApi } from '../api/resources';
import { blanchitAutour, cadreDuTicket, noirEtBlanc, sansGrossesTaches } from './ticket-image';

/** Plus grand côté d'une photo à lire : au-delà, la lecture traîne sans gagner. */
const COTE_MAX = 2000;

/**
 * Réduit une photo de téléphone avant la lecture.
 *
 * @param {Blob} fichier photo
 * @returns {Promise<Blob|HTMLCanvasElement>} l'image à lire
 */
async function image(fichier) {
  if (typeof createImageBitmap !== 'function') return fichier;
  try {
    const bmp = await bitmap(fichier);
    const echelle = Math.min(1, COTE_MAX / Math.max(bmp.width, bmp.height));
    if (echelle === 1) return fichier;
    const toile = document.createElement('canvas');
    toile.width = Math.round(bmp.width * echelle);
    toile.height = Math.round(bmp.height * echelle);
    toile.getContext('2d').drawImage(bmp, 0, 0, toile.width, toile.height);
    return toile;
  } catch {
    return fichier;
  }
}

/**
 * L'image d'une photo, tournée comme le téléphone l'a prise : une photo d'iPhone
 * porte son sens dans ses métadonnées, et dessinée sans lui, le ticket se
 * lisait couché.
 *
 * @param {Blob} fichier photo
 * @returns {Promise<ImageBitmap>} l'image
 */
async function bitmap(fichier) {
  try {
    return await createImageBitmap(fichier, { imageOrientation: 'from-image' });
  } catch {
    return createImageBitmap(fichier);
  }
}

/**
 * L'image tournée d'un quart de tour, d'un demi-tour ou de trois quarts.
 *
 * @param {Blob|HTMLCanvasElement} source photo ou image réduite
 * @param {number} degres 90, 180 ou 270
 * @returns {Promise<HTMLCanvasElement|Blob>} l'image tournée, ou la source si
 *   le navigateur ne sait pas la dessiner
 */
async function tournee(source, degres) {
  try {
    const dessin =
      typeof HTMLCanvasElement !== 'undefined' && source instanceof HTMLCanvasElement
        ? source
        : await bitmap(source);
    const couche = degres === 90 || degres === 270;
    const toile = document.createElement('canvas');
    toile.width = couche ? dessin.height : dessin.width;
    toile.height = couche ? dessin.width : dessin.height;
    const g = toile.getContext('2d');
    g.translate(toile.width / 2, toile.height / 2);
    g.rotate((degres * Math.PI) / 180);
    g.drawImage(dessin, -dessin.width / 2, -dessin.height / 2);
    return toile;
  } catch {
    return source;
  }
}

/** Plus grande image lue après agrandissement : la mémoire d'un téléphone a ses limites. */
const POINTS_MAX = 9_000_000;

/** En dessous de cette confiance, une ligne lue est le décor, pas le ticket. */
const CONFIANCE_MIN = 50;

/**
 * Les lectures d'une photo : le ticket découpé, en noir et blanc, agrandi une
 * fois et demie puis deux fois, lu en page puis en bloc. Chaque lecture se
 * trompe ailleurs ; lib/lecture-ticket.js garde ce qui se recoupe.
 */
const LECTURES = [
  { agrandi: 1.5, mode: '3' },
  { agrandi: 2, mode: '3' },
  { agrandi: 2, mode: '6' },
];

/**
 * La photo sur une toile, pour en lire les points.
 *
 * @param {Blob|HTMLCanvasElement} source photo ou image déjà dessinée
 * @returns {Promise<HTMLCanvasElement>} la toile
 */
async function enToile(source) {
  if (typeof HTMLCanvasElement !== 'undefined' && source instanceof HTMLCanvasElement)
    return source;
  const bmp = await bitmap(source);
  const toile = document.createElement('canvas');
  toile.width = bmp.width;
  toile.height = bmp.height;
  toile.getContext('2d').drawImage(bmp, 0, 0);
  return toile;
}

/**
 * Le ticket découpé dans la photo et préparé pour chaque lecture.
 *
 * @param {Blob|HTMLCanvasElement} source photo ou image tournée
 * @returns {Promise<Map<number, HTMLCanvasElement>>} l'image préparée, par agrandissement
 */
async function vuesDuTicket(source) {
  const toile = await enToile(source);
  const pinceau = toile.getContext('2d', { willReadFrequently: true });
  const cadre = cadreDuTicket(pinceau.getImageData(0, 0, toile.width, toile.height));
  const vues = new Map();
  for (const { agrandi } of LECTURES) {
    if (vues.has(agrandi)) continue;
    const f = Math.min(agrandi, Math.sqrt(POINTS_MAX / (cadre.w * cadre.h)));
    const vue = document.createElement('canvas');
    vue.width = Math.round(cadre.w * f);
    vue.height = Math.round(cadre.h * f);
    const g = vue.getContext('2d', { willReadFrequently: true });
    g.imageSmoothingQuality = 'high';
    g.drawImage(toile, cadre.x, cadre.y, cadre.w, cadre.h, 0, 0, vue.width, vue.height);
    const points = g.getImageData(0, 0, vue.width, vue.height);
    sansGrossesTaches(blanchitAutour(noirEtBlanc(points), cadre));
    g.putImageData(points, 0, 0);
    vues.set(agrandi, vue);
  }
  return vues;
}

/**
 * Le texte lu, sans les lignes trop incertaines : le décor autour du ticket
 * (« PP A », « "SEEN vus Jy ») passait pour le nom du commerce.
 *
 * @param {object} data résultat de tesseract.js
 * @returns {string} les lignes sûres
 */
function lignesSures(data) {
  const lignes = (data?.blocks || []).flatMap((b) =>
    (b.paragraphs || []).flatMap((p) => p.lines || []),
  );
  if (!lignes.length) return data?.text || '';
  return lignes
    .filter((l) => l.confidence >= CONFIANCE_MIN)
    .map((l) => String(l.text || '').trim())
    .filter(Boolean)
    .join('\n');
}

/**
 * Lit une photo de ticket ; si la lecture ne suffit pas, la relit tournée.
 *
 * <p>Un ticket photographié couché ou à l'envers se lisait en signes sans
 * suite (« EN gla) of [ERY… »), et ces signes remplissaient le libellé et le
 * fournisseur. La photo est relue d'un quart de tour dans chaque sens, puis à
 * l'envers, jusqu'à ce que la lecture trouve ce qu'il faut.
 *
 * <p>Chaque sens est lu plusieurs fois (voir {@link LECTURES}) : posé sur une
 * table à motifs, le ticket du Relais Tamoil se lisait « CN PEN » et 3 francs.
 * Un sens tourné n'est relu en entier que si sa première lecture trouve le
 * montant.
 *
 * @param {Blob} fichier photo du ticket
 * @param {(textes: string[]) => boolean} suffit vrai quand les textes lus suffisent
 * @returns {Promise<string[]>} le texte de chaque lecture : celles qui
 *   suffisent, sinon celles de la photo telle quelle
 */
export async function texteDeLaPhoto(fichier, suffit) {
  const source = await image(fichier);
  const { createWorker } = await import('tesseract.js');
  const travailleur = await createWorker(['fra', 'deu', 'ita', 'eng'], 1, {
    workerPath: '/ocr/worker.min.js',
    corePath: '/ocr',
    langPath: '/ocr/lang',
    workerBlobURL: false,
  });
  const lit = async (vue, mode) => {
    await travailleur.setParameters({ tessedit_pageseg_mode: mode });
    const { data } = await travailleur.recognize(vue, {}, { text: true, blocks: true });
    return lignesSures(data);
  };
  try {
    let premiers = null;
    for (const degres of [0, 90, 270, 180]) {
      const photo = degres ? await tournee(source, degres) : source;
      if (degres && photo === source) break;
      let vues;
      try {
        vues = await vuesDuTicket(photo);
      } catch {
        // Sans toile lisible, la photo se lit telle quelle, une fois.
        vues = null;
      }
      const lectures = vues ? LECTURES : [{ mode: '3' }];
      const textes = [];
      for (const [n, { agrandi, mode }] of lectures.entries()) {
        textes.push(await lit(vues ? vues.get(agrandi) : photo, mode));
        if (n === 0 && degres && !suffit(textes)) break;
      }
      if (premiers === null) premiers = textes;
      if (suffit(textes)) return textes;
    }
    return premiers || [];
  } finally {
    await travailleur.terminate();
  }
}

/**
 * Lit le texte d'une image.
 *
 * @param {Blob|HTMLCanvasElement} source photo ou page dessinée
 * @returns {Promise<string>} le texte, ligne par ligne
 */
async function texteDeLImage(source) {
  const { createWorker } = await import('tesseract.js');
  const travailleur = await createWorker(['fra', 'deu', 'ita', 'eng'], 1, {
    workerPath: '/ocr/worker.min.js',
    corePath: '/ocr',
    langPath: '/ocr/lang',
    workerBlobURL: false,
  });
  try {
    const { data } = await travailleur.recognize(source);
    return data?.text || '';
  } finally {
    await travailleur.terminate();
  }
}

/**
 * Lit le texte d'un PDF : sa couche de texte, sinon sa première page dessinée.
 *
 * @param {Blob} fichier PDF
 * @returns {Promise<string>} le texte
 */
async function texteDuPdf(fichier) {
  const [pdfjs, { default: Travailleur }] = await Promise.all([
    import('pdfjs-dist/legacy/build/pdf.mjs'),
    import('pdfjs-dist/legacy/build/pdf.worker.min.mjs?worker'),
  ]);
  if (!pdfjs.GlobalWorkerOptions.workerPort) {
    pdfjs.GlobalWorkerOptions.workerPort = new Travailleur();
  }
  const tache = pdfjs.getDocument({
    data: new Uint8Array(await fichier.arrayBuffer()),
    isEvalSupported: false,
  });
  try {
    const doc = await tache.promise;
    const lignes = [];
    for (let n = 1; n <= Math.min(doc.numPages, 3); n += 1) {
      const page = await doc.getPage(n);
      const contenu = await page.getTextContent();
      let ligne = '';
      for (const morceau of contenu.items) {
        ligne += morceau.str;
        if (morceau.hasEOL) {
          lignes.push(ligne);
          ligne = '';
        } else {
          ligne += ' ';
        }
      }
      if (ligne.trim()) lignes.push(ligne);
    }
    const texte = lignes.join('\n');
    if (texte.replace(/\s/g, '').length >= 20) return texte;
    // Un PDF scanné n'a pas de texte : sa première page se lit comme une photo.
    const page = await doc.getPage(1);
    const base = page.getViewport({ scale: 1 });
    const vue = page.getViewport({ scale: COTE_MAX / Math.max(base.width, base.height) });
    const toile = document.createElement('canvas');
    toile.width = Math.floor(vue.width);
    toile.height = Math.floor(vue.height);
    await page.render({ canvasContext: toile.getContext('2d'), viewport: vue }).promise;
    return texteDeLImage(toile);
  } finally {
    tache.destroy().catch(() => {});
  }
}

/**
 * Le texte d'un justificatif : photo, image ou PDF.
 *
 * @param {Blob & {name?: string}} fichier justificatif
 * @param {{serveur?: boolean}} [options] `serveur: false` lit le PDF dans le
 *   navigateur seulement (le serveur a déjà été interrogé)
 * @returns {Promise<string>} son texte
 */
export async function texteDuJustificatif(fichier, { serveur = true } = {}) {
  const pdf = fichier.type === 'application/pdf' || /\.pdf$/i.test(fichier.name || '');
  if (!pdf) return texteDeLImage(await image(fichier));
  // Le texte d'un PDF vient d'abord du serveur, qui le lit sur tous les
  // appareils : lu dans l'iPhone seulement, il échouait parfois et la pièce
  // restait vide. Un PDF scanné (sans texte), un compte sans accès ou un
  // serveur muet : le téléphone le lit lui-même.
  if (serveur) {
    try {
      const { texte } = await repriseApi.texteDuPdf(fichier, fichier.name, 30000);
      if (String(texte || '').replace(/\s/g, '').length >= 20) return texte;
    } catch {
      // Le téléphone prend le relais.
    }
  }
  return texteDuPdf(fichier);
}
